import React, { useState } from 'react';
import {
    showNiceDate,
    showTime,
    WidgetPanelDetailProps,
} from 'widgets/supportVisualisation/Content/Widgets/WidgetPanel';
import JsonPanel from 'utils/components/JsonPanel/JsonPanel';
import { PortfolioPlDataState } from 'widgets/portfolioPl/store/stream/PortfolioPlWidgetStreamState';
import { PortfolioPlWidgetConfigState } from 'widgets/portfolioPl/store/config/PortfolioPlWidgetConfigState';
import { getPortfolioPlData } from 'widgets/portfolioPl/portfolioPlData';
import { getBucketedTimeSeries } from 'widgets/portfolioPl/chart/leaves/buckets';
import { NumericTextInput } from '@instinet-analytics/semantic-ui-components';
import { WidgetPanelGrid } from 'widgets/supportVisualisation/Content/Widgets/WidgetPanelGrid';
import { ColDef } from 'ag-grid-community/dist/lib/entities/colDef';

export interface WidgetPanelPortfolioPlProps extends WidgetPanelDetailProps {
    mode:
        | 'data'
        | 'stream-timeseries'
        | 'data-timeseries'
        | 'data-bucketeddata';
}
export default function WidgetPanelPortfolioPl(
    props: WidgetPanelPortfolioPlProps
) {
    const {
        widgetState,
        appSettings,
        mode,
        widget: { id: widgetKey },
    } = props;

    const data = widgetState.stream.data as PortfolioPlDataState;
    const config = widgetState.config as PortfolioPlWidgetConfigState;
    const [chartWidth, setChartWidth] = useState(400);
    const [zoom, setZoom] = useState(1);

    if (!data || !config) {
        return <></>;
    }
    const portfolioPlData = getPortfolioPlData(
        data,
        config,
        appSettings.showBuysAsPositive
    );

    const bucketedData = getBucketedTimeSeries(
        portfolioPlData,
        chartWidth,
        zoom
    );

    for (const d of data.timeSeries) {
        showTime(d, '_i');
    }

    for (const b of portfolioPlData.timeSeries) {
        showNiceDate(b, 'time');
    }

    for (const b of portfolioPlData.breaksInTrading) {
        showNiceDate(b, 'from');
        showNiceDate(b, 'to');
    }
    for (const r of portfolioPlData.rangeLabels) {
        showNiceDate(r, 'open');
        showNiceDate(r, 'close');
    }
    for (const t of portfolioPlData.timeLineLabels) {
        showNiceDate(t, 'time');
    }
    for (const t of bucketedData) {
        showNiceDate(t, 'time');
    }

    const onChartWidthChange = (value: string) => {
        const i = parseInt(value);
        if (!isNaN(i)) {
            setChartWidth(i);
        }
    };
    const onZoomChange = (value: string) => {
        const i = parseInt(value);
        if (!isNaN(i)) {
            setZoom(i);
        }
    };

    const displayData = { portfolioPlData, bucketedData };

    const bucketedDataInput = (
        <>
            <div className="field">
                <span className={'label'}>Chart width:</span>
                <NumericTextInput
                    value={chartWidth}
                    onChange={onChartWidthChange}
                />
            </div>
            <div className="field">
                <span className={'label'}>Zoom:</span>
                <NumericTextInput value={zoom} onChange={onZoomChange} />
            </div>
        </>
    );

    switch (mode) {
        case 'data': {
            return (
                <div className="widget-panel ">
                    {bucketedDataInput}
                    <JsonPanel data={displayData} />
                </div>
            );
        }
        case 'stream-timeseries': {
            return (
                <WidgetPanelGrid
                    key="stream-timeseries"
                    data={data.timeSeries ?? []}
                    getRowDataId={(d) => d._i.toString()}
                    csvFileName={`${widgetKey}-stream-timeseries`}
                    colDefs={streamCols()}
                />
            );
        }
        case 'data-timeseries': {
            return (
                <WidgetPanelGrid
                    key="stream-timeseries"
                    data={portfolioPlData.timeSeries ?? []}
                    getRowDataId={(d) => d._i.toString()}
                    csvFileName={`${widgetKey}-data-timeseries`}
                    colDefs={timeSeriesCols()}
                />
            );
        }
        case 'data-bucketeddata': {
            return (
                <>
                    {bucketedDataInput}
                    <WidgetPanelGrid
                        key="stream-bucketedData"
                        data={bucketedData ?? []}
                        getRowDataId={(d) => d.time.toString()}
                        csvFileName={`${widgetKey}-data-bucketedData`}
                        colDefs={bucketedDataCols()}
                    />
                </>
            );
        }
        default:
            return (
                <div className="widget-panel widget-panel-fills">
                    Unrecognised mode {mode}
                </div>
            );
    }
}

export function addPortfolioPlPanes(
    detailProps: WidgetPanelDetailProps,
    panes: { menuItem: string; render: () => JSX.Element }[]
) {
    const render = () => (
        <WidgetPanelPortfolioPl {...detailProps} mode={'data'} />
    );
    panes.push({
        menuItem: 'Data',
        render,
    });

    const renderStream = () => (
        <WidgetPanelPortfolioPl {...detailProps} mode={'stream-timeseries'} />
    );
    panes.push({
        menuItem: 'Stream',
        render: renderStream,
    });

    const renderDataTs = () => (
        <WidgetPanelPortfolioPl {...detailProps} mode={'data-timeseries'} />
    );
    panes.push({
        menuItem: 'Mapped Time Series',
        render: renderDataTs,
    });

    const renderDataBuckets = () => (
        <WidgetPanelPortfolioPl {...detailProps} mode={'data-bucketeddata'} />
    );
    panes.push({
        menuItem: 'Bucketed Data',
        render: renderDataBuckets,
    });
}

function bucketedDataCols(): ColDef[] {
    return ['time', 'timeDisplay', 'buy', 'sell', 'net'].map((key) => ({
        field: key,
        key,
        width: 120,
        resizable: true,
    }));
}
function timeSeriesCols(): ColDef[] {
    return [
        '_i',
        'time',
        'timeDisplay',
        'realizedPnl',
        'pos-realizedPnl',
        'neg-realizedPnl',
        'totalPnl',
        'unrealizedPnl',
        'indexPnl.value',
        'indexPnl.isInterpolated',
        'buyLeaves',
        'sellLeaves',
        'netLeaves',
        'sessionBreakAnnotation',
    ].map((key) => ({
        field: key,
        key,
        width: 120,
        resizable: true,
    }));
}

function streamCols(): ColDef[] {
    return [
        '_i',
        '_iDisplay',
        'realizedPnlBuys',
        'realizedPnlSells',
        'realizedPnlBuysAndSells',
        'unrealizedPnlBuys',
        'unrealizedPnlSells',
        'unrealizedPnlBuysAndSells',
        'totalPnlBuys',
        'totalPnlSells',
        'totalPnlBuysAndSells',
        'indexPnl',
        'sessionBoundaryDate',
        'sessionBoundaryLabel',
        'buyUnrealizedBenchmark',
        'sellUnrealizedBenchmark',
        'percentCompleteBuys',
        'percentCompleteSells',
        'percentCompleteBuysAndSells',
        '_buyCumulativeQuantity',
        '_buyRealizedBenchmark',
        '_buyRealizedPnl',
        '_buyRemainingQuantity',
        '_buyUnrealizedPnl',
        '_sellCumulativeQuantity',
        '_sellRealizedBenchmark',
        '_sellRealizedPnl',
        '_sellRemainingQuantity',
        '_sellUnrealizedPnl',
        '_totalRealizedPnl',
        '_totalUnrealizedPnl',
        '_totalPnl',
    ].map((key) => ({
        field: key,
        key,
        width: 120,
        resizable: true,
    }));
}
